import { Clear } from "@mui/icons-material";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { AdornmentLens } from "@ryde/controls";
import { profileCompaniesDtoSelector, profileDtoAtom } from "@ryde/shared";

import {
  ProfileModalCompanies,
  ProfileModalLoading,
  ProfileModalNoCompany,
} from "./parts";
import {
  ProfileCompaniesSearchBlock,
  ProfileCompaniesTitleBlock,
  ProfileModalHeadStyled,
  ProfileScopeContainerStyled,
} from "./parts/profile.modal.styled";

export const ProfileModalContent: FC = () => {
  const profile = useRecoilValue(profileDtoAtom);
  const { t } = useTranslation();
  const isProfileListLoading = !profile;
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const companiesList = useRecoilValue(profileCompaniesDtoSelector);

  useEffect(() => {
    // Focus on the search input when component mounts
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);
  const companiesListFiltered = useMemo(() => {
    if (!searchText) {
      return companiesList;
    }

    if (!companiesList?.length) {
      return [];
    }

    return companiesList.filter(x =>
      x.title?.toLowerCase().includes(searchText?.toLowerCase()),
    );
  }, [companiesList, searchText]);
  return (
    <ProfileScopeContainerStyled>
      <ProfileModalHeadStyled>
        <ProfileCompaniesTitleBlock>
          <h3>{t("general.WELCOME", { username: profile?.username })}</h3>
          <h4>{t("general.SELECT_COMPANY")}</h4>
        </ProfileCompaniesTitleBlock>
        <ProfileCompaniesSearchBlock>
          <TextField
            fullWidth
            label={t("company.companyTitle")}
            value={searchText}
            data-e2e="search-company"
            autoComplete="off"
            size="small"
            inputRef={searchInputRef}
            onChange={e => setSearchText(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <AdornmentLens />,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t("company.clearSearch")}
                      onClick={() => setSearchText("")}
                      edge="end"
                    >
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </ProfileCompaniesSearchBlock>
      </ProfileModalHeadStyled>
      {!isProfileListLoading && !!companiesList?.length && (
        <ProfileModalCompanies companies={companiesListFiltered} />
      )}
      {!isProfileListLoading && !companiesList?.length && (
        <ProfileModalNoCompany />
      )}
      {isProfileListLoading && <ProfileModalLoading />}
    </ProfileScopeContainerStyled>
  );
};
